// @Version: 1.0.5
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ValidationErrors, NG_VALIDATORS, Validators, FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { FieldItemCheckboxService } from './checkbox.service';
import {  CustomValidatorsCheckBox } from './custom-validators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { IFieldItem , DIRECTION } from '../field-item.model';
import { filter, tap } from 'rxjs/operators';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';

@UntilDestroy()
@Component({
   selector: 'app-field-item-checkbox',
   templateUrl: './checkbox.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemCheckboxComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemCheckboxComponent),
         multi: true
      },
      FieldItemCheckboxService,
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemCheckboxComponent implements ControlValueAccessor, Validators, OnChanges, OnDestroy {
   
   public DIRECTION = DIRECTION;
   public TRANSLATE_PATH = TRANSLATE_PATH; 
   
   public done$$ = signal(false);
   public fgCheckbox: FormGroup = this.fb.group({});

   public hideRequireMaker: boolean = false;
   private fieldItem_: IFieldItem = {} as IFieldItem;

   @Input() clearValueIfDisabled: boolean = true;

   @Input()
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }

   @Output() isRequiredMarker = new EventEmitter<boolean>();

   get value (): string {
      return this.sFieldItemCheckbox.getSelection(this.fgCheckbox) ?? '';
   }

   constructor (private fb: FormBuilder, public sFieldItemCheckbox: FieldItemCheckboxService, private vCheckbox: CustomValidatorsCheckBox) { }
   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         if (changes.fieldItem.currentValue.valid) this.sFieldItemCheckbox.createValidList(changes.fieldItem.currentValue.valid);
         this.init(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }

   ngOnDestroy (): void {
      this.reset();
      this.sFieldItemCheckbox.reset(); 
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgCheckbox.updateValueAndValidity({emitEvent: false, onlySelf: true});
      return this._transformError(this.fgCheckbox, this.fieldItem.required ?? false);
   }

   private _transformError (control: FormControl | FormGroup, required: boolean): ValidationErrors | null {
      if (control.disabled) return null;
      let errors: ValidationErrors | null = Object.keys(control?.errors ?? {}).length == 0 
         ? required && (typeof this.value == undefined || this.value == null || (typeof this.value == 'string' && this.value.trim() == '') || (typeof this.value == 'object' && Object.keys(this.value).length == 0)) ? {required: true} : null
         : Object.keys(control?.errors ?? {}).length == 1 
            ? control.errors?.required ? {required: true} : {notAllowed: true}
            : control.errors?.required ? {required: true, notAllowed: true} : {notAllowed: true};
      
      if (!errors && control.pristine && required) errors = this._checkPristineRequired(control);
      return errors;
   }

   private _checkPristineRequired (control: FormControl | FormGroup): ValidationErrors | null {
      return control.disabled ? null : control.value == undefined || control.value == null || (typeof control.value == 'string' && control.value.trim() == '') || (typeof control.value == 'object' && Object.keys(control.value).length == 0) ? {required: true} : null;
   }

   writeValue (value: string | undefined): void {
      if(value == undefined) return;
      if (this.sFieldItemCheckbox.values.length == 0)  {
         return;
      }
      this.setCheckBoxValue(value);
   }

   private setCheckBoxValue (value: string | undefined | string): void {
      let check = {};
      this.sFieldItemCheckbox.values.forEach((checkValue, index) => {
         check = {...check, [index]: (value && value.search(checkValue.value) != -1) ? true : false}
      });

      this.fgCheckbox.setValue(check);
      if (typeof value == 'string' && value.trim().length > 0) {
         this.fgCheckbox.markAsDirty();
         this.fgCheckbox.markAsTouched();
         
      }

      this.onValidationChange();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn;
   }

   registerOnTouched (fn: any): void {
      this.onTouched = fn;
   }

   private onTouched () { }

   private onChange (_: string) { }

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (isDisabled) {
         this.fgCheckbox.disable();
         this.fgCheckbox.markAsDirty();
      }
      else this.fgCheckbox.enable();
      
      this.sFieldItemCheckbox.values.forEach((element, index) => {
         if (isDisabled && this.clearValueIfDisabled) this.fgCheckbox.get(index.toString())?.setValue("") ;
      });
      this.onValidationChange();
   }

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;;
   }
   
   onValidationChange: any = () => {}; 


   private reset (): void {
      this.isRequiredMarker.emit(false); 
      this.done$$.set(false);
      this.fgCheckbox! = this.fb.group({});
      this.fieldItem_ = {} as IFieldItem;
      this.sFieldItemCheckbox.reset();
      this.hideRequireMaker = false; 
   }

   /**
    * Attribut1 = List of Checkboxes
    * @returns 
    */
   private init (fieldItem: IFieldItem): boolean {
      if (!this.fieldItem.attribute_1) return false;

      if(fieldItem.content && typeof fieldItem.content == 'string' && fieldItem.content.trim().length > 0) this.setCheckBoxValue(fieldItem.content)
      this.initFormGroup(fieldItem);
      this.initFormControls(fieldItem);

      return this.sFieldItemCheckbox.createErrorMessages(this.fieldItem.attribute_2 || '');
   }

   private initFormGroup (fieldItem: IFieldItem): void {
      if (fieldItem.required) this.fgCheckbox.addValidators(this.vCheckbox.isRequired(this.fgCheckbox));
      if (fieldItem.valid) this.fgCheckbox.addValidators(this.vCheckbox.isValid(this.fgCheckbox, this.sFieldItemCheckbox.validList, this.sFieldItemCheckbox.values, fieldItem.attribute_3));

      this.fgCheckbox.valueChanges.subscribe(() => {
         if (!this.value && this.fgCheckbox.pristine) return;
         this.onChange(this.value);
         this.onTouched();
      });

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker);
   }

   private initFormControls (fieldItem: IFieldItem) {
      if (this.sFieldItemCheckbox.createValues(fieldItem.attribute_1 || '')) {
         const contentList = fieldItem.content && fieldItem.content != '' ? fieldItem.content.indexOf(';') == -1 ? [fieldItem.content] : fieldItem.content?.split(';') : [];
         this.sFieldItemCheckbox.values.forEach((item, index) => {
            this.fgCheckbox.addControl(index.toString(), this.fb.control(undefined))
         });
      }
   }
}
